import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, DatePicker, Form, Select, Space, Table, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import AsyncReportDownloadModal, { dashboardAsyncReportLabelI18nKeys } from '../../../components/asyncReportDownload';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { SearchOutlined } from '@ant-design/icons';
import type { PricingResultType } from '../settlement/graphql/mutations/pricingQueryMutations';
import { GET_ALL_PRICING_NODE_ENUMS, useFilteredPricingScenarios } from '../../../graphql/queries/settlement/getSettlementCommonQueries';
import { GET_ALL_FAIL_PRICING_RESULT_ERROR_CODES } from '../settlement/graphql/mutations/pricingQueryMutations';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import { PricingFailureClientSugSelector } from './components/PricingFailureClientSugSelector';
import { BILL_TIMEZONE_OPTIONS } from '../../../utils/timezoneFormatter';
import {
    EXPORT_PRICING_FAILURE_REVISION_BY_CONDITION,
    type ExportPricingFailureRevisionByConditionResponse,
    type ExportPricingFailureRevisionByConditionVariables
} from './graphql/mutations/exportPricingFailureRevisionByCondition';
import {
    QUERY_PRICING_FAILURE_REVISION_BY_CONDITION,
    type QueryPricingFailureRevisionByConditionResponse,
    type QueryPricingFailureRevisionByConditionVariables
} from './graphql/queries/queryPricingFailureRevisionByCondition';

const DEFAULT_BUSINESS_TIME_ZONE = 'Asia/Shanghai';

const formatWallClock = (d: dayjs.Dayjs): string => d.format('YYYY-MM-DD HH:mm:ss');

const PricingFailureExportPage: React.FC = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    dayjs.extend(utc);
    dayjs.extend(timezone);

    const hasRevenueAccess = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_PRICING_FAILURE_EXPORT]);
    const hasCostAccess = hasResource([ResourceCode.settlement.SETTLEMENT_COST_PRICING_FAILURE_EXPORT]);
    const canQuery = hasRevenueAccess || hasCostAccess;
    const canExport = hasResource([
        ResourceCode.settlement.SETTLEMENT_REVENUE_PRICING_FAILURE_EXPORT_OPERATE,
        ResourceCode.settlement.SETTLEMENT_COST_PRICING_FAILURE_EXPORT_OPERATE,
    ]);
    const { scenarioOptions, loading: scenarioLoading } = useFilteredPricingScenarios(hasRevenueAccess, hasCostAccess);
    const { data: errorCodeData, loading: errorCodeLoading } = useQuery(GET_ALL_FAIL_PRICING_RESULT_ERROR_CODES);
    const { data: pricingNodeData, loading: pricingNodeLoading } = useQuery(GET_ALL_PRICING_NODE_ENUMS);

    const errorCodeOptions = useMemo(() => {
        const list = errorCodeData?.getAllFailPricingResultErrorCodes || [];
        return list.map((item: { code: string; message: string }) => ({
            value: item.code,
            label: `${item.code} - ${item.message}`
        }));
    }, [errorCodeData]);

    const pricingNodeOptions = useMemo(() => {
        const list = pricingNodeData?.getAllPricingNodeEnums || [];
        return list.map((node: { code: string; description: string }) => ({
            value: node.code,
            label: node.description
        }));
    }, [pricingNodeData]);

    const [queryResult, setQueryResult] = useState<{
        data: PricingResultType[];
        totalCount: number;
        nextCursor: string | null;
    } | null>(null);

    const [queriedTimeZoneId, setQueriedTimeZoneId] = useState<string | null>(null);

    const BATCH_PAGES = 100;
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [downloadModalFileName, setDownloadModalFileName] = useState('');
    const [lastInput, setLastInput] = useState<{
        clientIds?: number[] | null;
        businessAtStart?: string | null;
        businessAtEnd?: string | null;
        businessTimeZoneId?: string | null;
        pricingErrorCode?: string | null;
        pricingNodeCode?: string | null;
        businessScenarioCode?: number | null;
        isSuccess?: boolean | null;
        pageSize: number;
    } | null>(null);
    const [queryPricingFailureRevisionByCondition, { loading: querying }] = useLazyQuery<
        QueryPricingFailureRevisionByConditionResponse,
        QueryPricingFailureRevisionByConditionVariables
    >(QUERY_PRICING_FAILURE_REVISION_BY_CONDITION, { fetchPolicy: 'network-only' });
    const [exportPricingFailureRevisionByCondition, { loading: exporting }] = useMutation<
        ExportPricingFailureRevisionByConditionResponse,
        ExportPricingFailureRevisionByConditionVariables
    >(EXPORT_PRICING_FAILURE_REVISION_BY_CONDITION);

    useEffect(() => {
        if (!scenarioOptions.length) return;
        const currentScenario = form.getFieldValue('businessScenarioCode');
        const currentTz = form.getFieldValue('businessTimeZoneId');
        const patch: Record<string, unknown> = {};
        if (currentScenario == null) {
            patch.businessScenarioCode = scenarioOptions[0].value;
        }
        if (currentTz == null) {
            patch.businessTimeZoneId = DEFAULT_BUSINESS_TIME_ZONE;
        }
        if (Object.keys(patch).length) {
            form.setFieldsValue(patch);
        }
    }, [scenarioOptions, form]);

    const selectedScenarioCode: number | undefined = Form.useWatch('businessScenarioCode', form);

    useEffect(() => {
        form.setFieldsValue({ clientIds: [] });
    }, [selectedScenarioCode, form]);

    const buildConditionInput = useCallback(
        (values: Record<string, unknown>, pageSizeValue: number, cursor?: string | null) => {
            const range = values.businessTimeRange as [dayjs.Dayjs, dayjs.Dayjs] | undefined;
            const businessTimeZoneId = String(values.businessTimeZoneId ?? '').trim();
            const businessAtStart = range?.[0] ? formatWallClock(range[0]) : null;
            const businessAtEnd = range?.[1] ? formatWallClock(range[1]) : null;

            const pricingErrorCodes: string[] = Array.isArray(values.pricingErrorCodes)
                ? (values.pricingErrorCodes as string[])
                : [];
            const pricingErrorCode = pricingErrorCodes.length ? pricingErrorCodes.join(',') : null;

            const rawClientIds: string[] = Array.isArray(values.clientIds)
                ? (values.clientIds as string[])
                : values.clientIds
                  ? [String(values.clientIds)]
                  : [];
            const clientIdsNums = rawClientIds
                .map((id) => Number(String(id).trim()))
                .filter((n) => Number.isFinite(n));
            const clientIds = clientIdsNums.length ? clientIdsNums : null;

            return {
                clientIds,
                businessAtStart,
                businessAtEnd,
                businessTimeZoneId,
                pricingErrorCode,
                pricingNodeCode: (values.pricingNodeCode as string) || null,
                businessScenarioCode:
                    values.businessScenarioCode != null
                        ? Math.trunc(Number(values.businessScenarioCode))
                        : null,
                isSuccess: false,
                page: 1,
                pageSize: pageSizeValue,
                cursor: cursor ?? null,
                batchPages: BATCH_PAGES
            };
        },
        []
    );

    const runQuery = useCallback(
        async (values: Record<string, unknown>, cursor?: string | null) => {
            const input = buildConditionInput(values, pageSize, cursor);

            const { data } = await queryPricingFailureRevisionByCondition({ variables: { input } });
            const res = data?.queryPricingFailureRevisionByCondition;
            if (res && !res.success) {
                message.warning(res.message || '查询失败');
            }
            if (res?.success) {
                if (cursor && queryResult) {
                    setQueryResult({
                        data: queryResult.data.concat(res.data || []),
                        totalCount: queryResult.totalCount,
                        nextCursor: res.nextCursor ?? null
                    });
                } else {
                    setLastInput({
                        clientIds: input.clientIds,
                        businessAtStart: input.businessAtStart,
                        businessAtEnd: input.businessAtEnd,
                        businessTimeZoneId: input.businessTimeZoneId,
                        pricingErrorCode: input.pricingErrorCode,
                        pricingNodeCode: input.pricingNodeCode,
                        businessScenarioCode: input.businessScenarioCode,
                        isSuccess: false,
                        pageSize
                    });
                    setQueriedTimeZoneId(input.businessTimeZoneId ?? null);
                    setPage(1);
                    setQueryResult({
                        data: res.data || [],
                        totalCount: res.totalCount ?? 0,
                        nextCursor: res.nextCursor ?? null
                    });
                }
            } else if (!cursor) {
                setQueryResult({
                    data: [],
                    totalCount: 0,
                    nextCursor: null
                });
                setQueriedTimeZoneId(null);
            }
        },
        [buildConditionInput, pageSize, queryPricingFailureRevisionByCondition, queryResult]
    );

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setQueryResult(null);
            await runQuery(values, null);
        } catch {
            // ignore
        }
    };

    const handleReset = () => {
        form.resetFields(['clientIds', 'businessTimeRange', 'pricingErrorCodes', 'pricingNodeCode']);
        form.setFieldsValue({ businessTimeZoneId: DEFAULT_BUSINESS_TIME_ZONE });
        setQueryResult(null);
        setLastInput(null);
        setQueriedTimeZoneId(null);
        setPage(1);
    };

    const handleLoadNextBatch = useCallback(
        async (cursor: string) => {
            if (!lastInput || !queryResult) return;
            const input = {
                clientIds: lastInput.clientIds ?? null,
                businessAtStart: lastInput.businessAtStart ?? null,
                businessAtEnd: lastInput.businessAtEnd ?? null,
                businessTimeZoneId: lastInput.businessTimeZoneId ?? null,
                pricingErrorCode: lastInput.pricingErrorCode ?? null,
                pricingNodeCode: lastInput.pricingNodeCode ?? null,
                businessScenarioCode: lastInput.businessScenarioCode ?? null,
                isSuccess: lastInput.isSuccess ?? null,
                page: 1,
                pageSize: lastInput.pageSize,
                cursor,
                batchPages: BATCH_PAGES
            };
            const { data } = await queryPricingFailureRevisionByCondition({ variables: { input } });
            const res = data?.queryPricingFailureRevisionByCondition;
            if (res && !res.success) {
                message.warning(res.message || '查询失败');
                return;
            }
            if (res?.success) {
                setQueryResult({
                    data: queryResult.data.concat(res.data || []),
                    totalCount: queryResult.totalCount,
                    nextCursor: res.nextCursor ?? null
                });
            }
        },
        [BATCH_PAGES, lastInput, queryPricingFailureRevisionByCondition, queryResult]
    );

    const handleExportExcel = async () => {
        try {
            const values = await form.validateFields();
            const input = buildConditionInput(values, 200);
            const { data } = await exportPricingFailureRevisionByCondition({ variables: { input } });
            const taskId = data?.exportPricingFailureRevisionByCondition;
            if (taskId) {
                setDownloadModalFileName(`pricing_failure_revision_export_${taskId}.xlsx`);
                setDownloadModalVisible(true);
            }
        } catch {
            // ignore
        }
    };

    const timeZoneDisplayLabel = useMemo(() => {
        const id = queriedTimeZoneId ?? '';
        const label = BILL_TIMEZONE_OPTIONS.find((o) => o.value === id)?.label;
        return label || id || '—';
    }, [queriedTimeZoneId]);

    const columns = useMemo(() => {
        const getPricingNodeDescription = (code: string | null | undefined) => {
            if (!code) return '-';
            const node = pricingNodeOptions.find((o: { value: string }) => o.value === code);
            return node ? node.label : code;
        };

        return [
            { title: '运单号', dataIndex: 'businessId', key: 'businessId', width: 160 },
            { title: '外部订单号', dataIndex: 'otherBusinessId', key: 'otherBusinessId', width: 160 },
            { title: '客户ID', dataIndex: 'clientId', key: 'clientId', width: 90 },
            { title: '业务费用项', dataIndex: 'businessFeeCode', key: 'businessFeeCode', width: 110 },
            {
                title: '计费节点',
                dataIndex: 'pricingNodeCode',
                key: 'pricingNodeCode',
                width: 140,
                render: (code: string | null | undefined) => getPricingNodeDescription(code)
            },
            {
                title: '计费失败错误码',
                dataIndex: 'pricingErrorCode',
                key: 'pricingErrorCode',
                width: 160,
                render: (code: string | null | undefined) => code || '-'
            },
            {
                title: '时区',
                key: 'timeZone',
                width: 180,
                render: () => timeZoneDisplayLabel
            },
            {
                title: '业务时间',
                dataIndex: 'businessAt',
                key: 'businessAt',
                width: 170,
                render: (date: string | null | undefined) => date?.trim() || '—'
            }
        ];
    }, [pricingNodeOptions, timeZoneDisplayLabel]);

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <div className="mb-6">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            {t('settlement.revise.pricingFailureExport')}
                        </Typography.Title>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <Typography.Text type="secondary">业务场景</Typography.Text>
                        <Select
                            style={{ width: 240 }}
                            loading={scenarioLoading}
                            options={scenarioOptions}
                            placeholder="请选择业务场景"
                            value={selectedScenarioCode}
                            onChange={(v) => form.setFieldsValue({ businessScenarioCode: v })}
                        />
                    </div>
                </div>
            </div>

            <Alert
                type="info"
                showIcon
                message="说明"
                description="TODO 对于该页面的一些说明，还没想好说什么，暂时先空着。。。"
                className="mb-4"
            />

            <Card title="查询条件" className="mt-2">
                <Form form={form} layout="vertical">
                    <Form.Item name="businessScenarioCode" hidden>
                        <input />
                    </Form.Item>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Form.Item label="客户（支持SUG，多选）" name="clientIds">
                            <PricingFailureClientSugSelector
                                businessScenarioCode={selectedScenarioCode}
                                allowClear
                                placeholder="请选择客户（默认空）"
                                mode="multiple"
                            />
                        </Form.Item>

                        <Form.Item
                            label="时区"
                            name="businessTimeZoneId"
                            rules={[{ required: true, message: '请选择时区' }]}
                        >
                            <Select
                                options={[...BILL_TIMEZONE_OPTIONS]}
                                placeholder="请选择时区"
                            />
                        </Form.Item>

                        <Form.Item
                            label="业务时间"
                            name="businessTimeRange"
                            rules={[
                                { required: true, message: '请选择业务时间范围' },
                                {
                                    validator: async (_, value) => {
                                        if (
                                            !value ||
                                            !Array.isArray(value) ||
                                            value.length < 2 ||
                                            !value[0] ||
                                            !value[1]
                                        ) {
                                            throw new Error('请选择完整的开始与结束时间');
                                        }
                                    }
                                }
                            ]}
                        >
                            <DatePicker.RangePicker
                                style={{ width: '100%' }}
                                showTime={{ format: 'HH:mm:ss' }}
                                format="YYYY-MM-DD HH:mm:ss"
                                placeholder={['开始时间', '结束时间']}
                                onChange={(dates) => {
                                    form.setFieldsValue({ businessTimeRange: dates });
                                }}
                            />
                        </Form.Item>

                        <Form.Item label="计费失败错误码（多选）" name="pricingErrorCodes">
                            <Select
                                mode="multiple"
                                placeholder="请选择"
                                allowClear
                                loading={errorCodeLoading}
                                options={errorCodeOptions}
                                maxTagCount="responsive"
                            />
                        </Form.Item>

                        <Form.Item label="计费节点" name="pricingNodeCode">
                            <Select
                                placeholder="请选择"
                                allowClear
                                loading={pricingNodeLoading}
                                options={pricingNodeOptions}
                            />
                        </Form.Item>
                    </div>

                    <div className="mt-6">
                        <Space>
                            {canQuery && (
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    loading={querying}
                                    size="large"
                                    onClick={handleSubmit}
                                >
                                    查询
                                </Button>
                            )}
                            {canExport && (
                                <Button size="large" onClick={handleExportExcel} disabled={exporting}>
                                    导出查询订正数据
                                </Button>
                            )}
                            {canQuery && (
                                <Button size="large" onClick={handleReset}>
                                    重置
                                </Button>
                            )}
                        </Space>
                    </div>
                </Form>
            </Card>

            <Card
                title={
                    queryResult
                        ? `查询结果（共 ${queryResult.totalCount} 条，已加载 ${queryResult.data.length} 条）`
                        : '查询结果'
                }
                className="mt-4"
            >
                <Table
                    loading={querying}
                    columns={columns as any}
                    dataSource={queryResult ? queryResult.data.slice((page - 1) * pageSize, page * pageSize) : []}
                    rowKey={(r: PricingResultType, index?: number) => `${r.id ?? 'na'}_${index ?? 0}`}
                    pagination={{
                        current: page,
                        pageSize,
                        total: queryResult?.totalCount ?? 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条，已加载 ${queryResult?.data.length ?? 0} 条`,
                        onChange: (p, size) => {
                            const nextSize = size || pageSize;
                            setPage(p);
                            setPageSize(nextSize);
                            const needEndIndex = p * nextSize;
                            if (queryResult && needEndIndex > queryResult.data.length && queryResult.nextCursor) {
                                handleLoadNextBatch(queryResult.nextCursor);
                            }
                        }
                    }}
                    scroll={{ x: 1380 }}
                />
            </Card>

            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={downloadModalFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.pricingFailureRevisionExport}
            />
        </div>
    );
};

export default PricingFailureExportPage;